/**
 * The readout under the crosshair: when, how fast, and what went wrong.
 *
 * Styled like the kit's `ChartTooltipContent`, but written out, because a
 * bucket carries more than a value per series: its median leads (the value
 * is the strong element, the label follows), then the p95 and the extremes,
 * then how many checks it holds and how many failed or could not tell, then
 * why the latest failure failed. A raw check is just its time and value.
 *
 * A point without an answer does not say "0 ms" or "-": it says what the lane
 * says about it (down, or the site could not be seen), and the check code
 * says why.
 */
import { formatMs, laneTone, type ChartRow } from "../../../../lib/monitoring";
import { codeText, type T } from "../words";
import { pointTime } from "./labels";

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-mono tabular-nums text-foreground">{value}</dd>
    </>
  );
}

export function SeriesTooltip({
  active,
  row,
  step,
  withDay,
  t,
}: {
  active: boolean;
  row: ChartRow | null;
  step: number;
  /** The window is long enough that the axis no longer says which day it is. */
  withDay: boolean;
  t: T;
}) {
  const p = row?.point;
  if (!active || !p) return null;
  const answered = p.p50 !== null;
  const why = codeText(t, p.code);
  const blind = p.blocked + p.unknown;
  // Only a bucket prints these; a raw point is one check and its lane word already says how it went.
  const counts = [
    t("monitoring.chart.checks", { n: p.n }),
    p.down > 0 ? t("monitoring.chart.failedChecks", { n: p.down }) : null,
    blind > 0 ? t("monitoring.chart.blind", { n: blind }) : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="grid min-w-[9.5rem] max-w-[16rem] gap-1.5 rounded-lg border border-border/50 bg-card px-2.5 py-1.5 text-xs shadow-xl">
      <div className="crm-tnum font-medium">{pointTime(p, step, withDay)}</div>
      {answered ? (
        <div className="flex items-center gap-2">
          <span aria-hidden className="h-0.5 w-3 shrink-0 rounded-full bg-[var(--color-p50)]" />
          <span className="font-mono font-semibold tabular-nums text-foreground">{formatMs(p.p50)}</span>
          <span className="text-muted-foreground">{t(step > 0 ? "monitoring.chart.median" : "monitoring.chart.answered")}</span>
        </div>
      ) : (
        <div className="font-semibold text-foreground">{t(`monitoring.chart.lane.${laneTone(p)}`)}</div>
      )}
      {step > 0 && answered ? (
        <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <Figure label={t("monitoring.chart.p95")} value={formatMs(p.p95)} />
          <Figure label={t("monitoring.chart.slowest")} value={formatMs(p.max)} />
          <Figure label={t("monitoring.chart.fastest")} value={formatMs(p.min)} />
        </dl>
      ) : null}
      {step > 0 ? <div className="crm-tnum text-muted-foreground">{counts.join(" · ")}</div> : null}
      {why ? <div className="text-muted-foreground">{why}</div> : null}
    </div>
  );
}
